import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import CalendarPage from './pages/CalendarPage';
import EmployeesPage from './pages/EmployeesPage';
import BranchManagementPage from './pages/BranchManagementPage';
import BirdEyeViewPage from './pages/BirdEyeViewPage';
import MultiBranchManagementPage from './pages/MultiBranchManagementPage';
import SummaryPage from './pages/SummaryPage';
import PublicHolidaysPage from './pages/PublicHolidaysPage';
import LeaveManagementPage from './pages/LeaveManagementPage';
import AuditLogsPage from './pages/AuditLogsPage';
import LoginPage from './pages/LoginPage';
import LarkCallbackPage from './pages/LarkCallbackPage';
import 'react-toastify/dist/ReactToastify.css';

// Global theme with Sarabun font and modern color scheme
const theme = {
  fonts: {
    main: "'Sarabun', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
  },
  fontWeights: {
    light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800
  },
  colors: {
    primary: '#930c00',        // Brand red — used sparingly (primary actions, active, key accents)
    primaryHover: '#7a0a00',   // Darker red for hover / press feedback
    primarySoft: '#fbeae7',    // Soft red tint for selected / hover backgrounds
    secondary: '#efe7d2',      // Brand cream — warm accent
    white: '#ffffff',
    background: '#f7f6f3',      // Warm neutral app background (calm, flat)
    surface: '#ffffff',        // Cards / surfaces
    surfaceAlt: '#faf9f5',     // Secondary surface (subtle insets / zebra)
    accent: '#7a0a00',         // Deep red (press state; kept for existing refs)
    text: {
      primary: '#2b2825',      // Warm near-black
      secondary: '#6b645c',    // Muted body text
      light: '#9c948a',        // Subtle / placeholder
      muted: '#9c948a',        // alias of light
      inverse: '#ffffff'
    },
    border: {
      light: '#e7e3db',        // Warm hairline
      medium: '#d8d3c8',
      dark: '#b8b1a5'
    },
    status: {
      success: '#2e7d5b',
      warning: '#b26a12',
      error: '#c5372c',
      info: '#3a6ea5'
    },
    // Categorical colours for calendar events / charts (solid, calm)
    event: {
      dayShift: '#2f6fb3',
      noonShift: '#c17a1a',
      teamDayShift: '#8fb4dd',
      teamNoonShift: '#e3c07a',
      leave: '#2e7d5b',
      holiday: '#930c00'
    }
  },
  shadows: {
    light: '0 1px 2px rgba(43,40,37,0.06)',
    medium: '0 4px 12px rgba(43,40,37,0.08)',
    heavy: '0 12px 32px rgba(43,40,37,0.12)',
    card: '0 1px 2px rgba(43,40,37,0.04), 0 1px 3px rgba(43,40,37,0.06)'
  },
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    xl: '16px'
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  }
};

// Global styles to ensure Sarabun font is used everywhere with modern styling
const GlobalStyle = createGlobalStyle`
  * {
    font-family: ${props => props.theme.fonts.main};
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    background: ${props => props.theme.colors.background};
    min-height: 100vh;
    line-height: 1.6;
    color: ${props => props.theme.colors.text.primary};
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.colors.text.primary};
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 0 0 ${props => props.theme.spacing.md} 0;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: ${props => props.theme.borderRadius.small};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.border.dark};
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  display: flex;
  font-family: ${props => props.theme.fonts.main};
  position: relative;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.$withSidebar ? '280px' : '0'};
  min-height: 100vh;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: ${props => props.theme.fonts.main};
  position: relative;
  
  @media (max-width: 1024px) {
    margin-left: ${props => props.$withSidebar ? '260px' : '0'};
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 0;
  width: 100%;
  max-width: none;
  font-family: ${props => props.theme.fonts.main};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const MainApp = () => {
  const { isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Show loading screen while checking authentication status
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppContainer>
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      <MainContent $withSidebar={true}>
        <ContentWrapper>
          <Routes>
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/branches" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <BranchManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/bird-eye-view" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <BirdEyeViewPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/multi-branch" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <MultiBranchManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/employees" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <EmployeesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/holidays" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <PublicHolidaysPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/leave-management" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <LeaveManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/audit-logs" 
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <AuditLogsPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/summary"
              element={
                <ProtectedRoute requiredRoles={['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']}>
                  <SummaryPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ContentWrapper>
      </MainContent>
    </AppContainer>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/lark/callback" element={<LarkCallbackPage />} />
            
            {/* Protected routes */}
            <Route path="/*" element={<MainApp />} />
          </Routes>
          <ToastContainer position="top-right" autoClose={3000} />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;